import type { ProjectGraphNode } from '@avisoma/shared';

type PartNode = Extract<ProjectGraphNode, { kind: 'part' }>;

/** The life line of a part: life limit, CSN and remaining cycles with the thrust, each only when stated. */
function lifeText(node: PartNode): string | undefined {
  const life = node.life;
  if (life === undefined) return undefined;
  const pieces = [
    life.lifeLimitCycles === undefined ? undefined : `Life limit ${life.lifeLimitCycles} FC`,
    life.csn === undefined ? undefined : `CSN ${life.csn}`,
    life.remainingCycles === undefined ? undefined : `${life.remainingCycles} FC remaining`,
  ].filter((piece): piece is string => piece !== undefined);
  const text =
    pieces.length === 0
      ? life.thrust === undefined
        ? undefined
        : `Thrust ${life.thrust}`
      : `${pieces.join(' · ')}${life.thrust === undefined ? '' : ` (${life.thrust})`}`;
  if (text === undefined) return undefined;
  return node.asOf === undefined ? text : `${text}, as of ${node.asOf}`;
}

/**
 * The back-to-birth line. An engine verdict (it carries `result`): the verdict, a gap's reasons in brackets, then the
 * class and the result ("Back-to-birth: gap (date-order) · class A · GAP"). A legacy verdict: the verdict, and for a
 * gap whether the part has a movement card.
 */
function btbText(node: PartNode): string | undefined {
  const btb = node.btb;
  if (btb === undefined) return undefined;
  if (btb.result !== undefined) {
    const reasons = btb.verdict === 'gap' && btb.gapReasons?.length ? ` (${btb.gapReasons.join(', ')})` : '';
    const pieces = [`Back-to-birth: ${btb.verdict}${reasons}`, btb.class && `class ${btb.class}`, btb.result];
    return pieces.filter((piece) => piece !== undefined).join(' · ');
  }
  if (btb.verdict !== 'gap') return `Back-to-birth: ${btb.verdict}`;
  const basis = { record: 'card present', absent: 'no card', inferred: 'inferred' }[btb.basis];
  return `Back-to-birth: gap (${basis})`;
}

/**
 * The selected part's detail lines: its life data from the LLP sheet ("Life limit 20000 FC · CSN 3000 · 17000 FC
 * remaining (7B27), as of 2024-03-01"), its back-to-birth verdict ("Back-to-birth: gap (no card)") and the other
 * engines it was on ("Previously on ESN 888888, V777777"), each only when the graph states it.
 */
export function partDetailLines(node: ProjectGraphNode): string[] {
  if (node.kind !== 'part') return [];
  const life = lifeText(node);
  const previous = node.previousEsns?.length ? `Previously on ESN ${node.previousEsns.join(', ')}` : undefined;
  return [life, btbText(node), previous].filter((line): line is string => line !== undefined);
}
